<template>
  <a-form-model :model="form" :label-col="labelCol" :wrapper-col="wrapperCol">
    <a-form-model-item label="选择用户" required>
      <a-select
        v-model="form.userId"
        show-search
        :disabled="lockUser"
        :get-popup-container="selectPopupContainer"
        placeholder="输入邮箱搜索 C 端用户"
        :filter-option="false"
        :not-found-content="userLoading ? undefined : null"
        @search="searchUser"
        style="width: 100%"
      >
        <a-spin v-if="userLoading" slot="notFoundContent" size="small" />
        <a-select-option v-for="u in userOptions" :key="u.id" :value="u.id">
          {{ u.email }} {{ u.nickname ? '(' + u.nickname + ')' : '' }}
        </a-select-option>
      </a-select>
    </a-form-model-item>
    <a-form-model-item label="国家" required>
      <a-select
        v-model="form.countryId"
        placeholder="请选择国家"
        :loading="countryLoading"
        :get-popup-container="selectPopupContainer"
        style="width: 100%"
        @change="onCountryChange"
      >
        <a-select-option v-for="c in countryOptions" :key="c.id" :value="c.id">
          {{ c.name }}{{ c.currencySymbol ? ' (' + c.currencySymbol + ')' : '' }}
        </a-select-option>
      </a-select>
    </a-form-model-item>
    <a-form-model-item label="卡种" required>
      <a-select
        v-model="form.categoryId"
        :placeholder="ratePlaceholder"
        :loading="rateLoading"
        :disabled="!form.countryId"
        :get-popup-container="selectPopupContainer"
        style="width: 100%"
        @change="onCategoryChange"
      >
        <a-select-option v-for="g in giftRates" :key="g.categoryId" :value="g.categoryId">
          {{ g.categoryName }}（参考价 {{ formatRate(g.rate) }}）
        </a-select-option>
      </a-select>
      <div v-if="!rateLoading && form.countryId && giftRates.length === 0" class="form-tip">
        当前国家下暂无可用卡种，请先在「礼品卡管理 → 卡种汇率 → 面额阶梯价」中配置
      </div>
    </a-form-model-item>
    <a-divider>礼品卡明细</a-divider>
    <div v-for="(card, idx) in form.cards" :key="idx" class="card-block">
      <div class="card-block-title">第 {{ idx + 1 }} 张</div>
      <a-row :gutter="12" align="middle" style="margin-bottom: 8px">
        <a-col :span="8">
          <span class="field-label">卡类型 <span class="required">*</span></span>
          <a-radio-group v-model="card.cardType" @change="onCardDetailChange">
            <a-radio :value="1">实体卡</a-radio>
            <a-radio :value="2">卡密</a-radio>
          </a-radio-group>
        </a-col>
        <a-col :span="8">
          <span class="field-label">Redeem Value <span class="required">*</span></span>
          <a-input-number v-model="card.faceValue" :min="1" placeholder="面额" style="width: 100%" @change="onCardDetailChange" />
        </a-col>
        <a-col :span="8" class="card-actions">
          <a-button type="link" @click="removeCard(idx)" v-if="form.cards.length > 1">删除</a-button>
        </a-col>
      </a-row>
      <a-row :gutter="12" align="middle">
        <a-col :span="7">
          <a-input v-model="card.cardNumber" placeholder="卡号" />
        </a-col>
        <a-col :span="5">
          <a-input v-model="card.cardExpiry" placeholder="有效期" />
        </a-col>
        <a-col :span="5">
          <a-input v-model="card.cardPin" placeholder="PIN" />
        </a-col>
        <a-col :span="7" class="card-actions">
          <a-button type="link" @click="removeCard(idx)" v-if="form.cards.length > 1">删除</a-button>
        </a-col>
      </a-row>
      <div class="card-images">
        <span class="card-images-label">礼品卡图片</span>
        <file-upload
          :value="card.imgUrls"
          type="image"
          :count="9"
          field-name="imgUrls"
          @change="val => onCardImagesChange(idx, val)"
        />
      </div>
    </div>
    <a-button type="dashed" block icon="plus" @click="addCard">添加礼品卡</a-button>
    <a-form-model-item label="到账金额" style="margin-top: 16px">
      <a-button :disabled="!form.userId" @click="doCalculate">计算到账</a-button>
      <span v-if="calcResult" class="calc-result">
        预计到账 {{ calcResult.currencySymbol }}{{ calcResult.cashAmount }}
        <span v-if="calcResult.cardBreakdown && calcResult.cardBreakdown.length > 1" class="calc-breakdown">
          （{{ calcResult.cardBreakdown.join(' + ') }}）
        </span>
      </span>
      <div class="form-tip">到账 = 各张 Redeem Value × 对应默认价格 之和</div>
    </a-form-model-item>
    <a-form-model-item label="备注" style="margin-top: 16px">
      <a-textarea v-model="form.comment" rows="2" placeholder="选填" />
    </a-form-model-item>
    <a-form-model-item :wrapper-col="submitWrapperCol">
      <a-button type="primary" :loading="submitting" @click="submit">提交订单</a-button>
    </a-form-model-item>
  </a-form-model>
</template>

<script>
import { pageUser, getUser } from '@/api/biz/user'
import { csCalculate, csSubmitOrder, giftRates, csCountries } from '@/api/customer-service/order'

/** 雪花 ID 须保持字符串，勿 Number() 以免精度丢失 */
function toUserIdString(id) {
  if (id == null || id === '') return ''
  return String(id).trim()
}

const emptyCard = () => ({
  cardType: 1,
  cardNumber: '',
  cardExpiry: '',
  cardPin: '',
  imgUrls: '',
  faceValue: undefined
})

const emptyForm = () => ({
  userId: undefined,
  countryId: undefined,
  categoryId: undefined,
  comment: '',
  cards: [emptyCard()]
})

export default {
  name: 'CsOrderCreateForm',
  props: {
    /** 预填用户 ID（客服聊天带入） */
    initialUserId: {
      type: [Number, String],
      default: undefined
    },
    /** 锁定用户选择（聊天代客场景） */
    lockUser: {
      type: Boolean,
      default: false
    },
    /** 抽屉内紧凑布局 */
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      userLoading: false,
      countryLoading: false,
      rateLoading: false,
      ratesRequestSeq: 0,
      userOptions: [],
      countryOptions: [],
      giftRates: [],
      calcResult: null,
      submitting: false,
      form: emptyForm()
    }
  },
  computed: {
    ratePlaceholder() {
      if (!this.form.countryId) return '请先选择国家'
      if (this.rateLoading) return '加载中…'
      return '请选择卡种'
    },
    labelCol() {
      return this.compact ? { span: 6 } : { span: 4 }
    },
    wrapperCol() {
      return this.compact ? { span: 18 } : { span: 16 }
    },
    submitWrapperCol() {
      return this.compact ? { span: 18, offset: 6 } : { offset: 4 }
    },
    selectPopupContainer() {
      return this.compact
        ? (triggerNode) => (triggerNode && triggerNode.parentNode) || document.body
        : (triggerNode) => triggerNode.parentNode
    }
  },
  watch: {
    initialUserId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.prefillUser(val)
        }
      }
    }
  },
  created() {
    this.loadCountries()
  },
  methods: {
    loadCountries() {
      this.countryLoading = true
      csCountries().then(res => {
        this.countryOptions = (res.data || []).map(c => ({
          ...c,
          id: c.id != null ? Number(c.id) : c.id
        }))
      }).catch((err) => {
        this.countryOptions = []
        this.$message.error((err && err.message) || '加载国家列表失败')
      }).finally(() => {
        this.countryLoading = false
      })
    },
    formatRate(rate) {
      if (rate == null || rate === '') return '—'
      const n = Number(rate)
      return Number.isFinite(n) ? n : String(rate)
    },
    normalizeGiftRates(list) {
      return (list || []).map(item => ({
        ...item,
        categoryId: item.categoryId != null ? Number(item.categoryId) : item.categoryId,
        countryId: item.countryId != null ? Number(item.countryId) : item.countryId
      }))
    },
    reset() {
      this.form = emptyForm()
      this.calcResult = null
      this.giftRates = []
      if (this.initialUserId) {
        this.prefillUser(this.initialUserId)
      }
    },
    prefillUser(userId) {
      const id = toUserIdString(userId)
      if (!/^\d+$/.test(id)) return
      getUser({ id }).then(res => {
        const u = res.data
        if (!u) return
        // 使用请求时的 ID，避免响应 JSON 中大整数被解析为 number 后精度丢失
        this.form.userId = id
        const exists = this.userOptions.some(item => toUserIdString(item.id) === id)
        if (!exists) {
          this.userOptions = [{ id, email: u.email, nickname: u.nickname }].concat(this.userOptions)
        }
      })
    },
    searchUser(keyword) {
      if (this.lockUser) return
      if (!keyword || keyword.length < 2) return
      this.userLoading = true
      pageUser({ email: keyword, pageNum: 1, pageSize: 20 }).then(res => {
        const records = (res.data && res.data.records) || []
        this.userOptions = records.map(u => ({
          ...u,
          id: toUserIdString(u.id)
        }))
      }).finally(() => { this.userLoading = false })
    },
    onCountryChange() {
      this.form.categoryId = undefined
      this.calcResult = null
      this.giftRates = []
      if (this.form.countryId) {
        this.loadRates()
      }
    },
    onCardDetailChange() {
      this.calcResult = null
    },
    loadRates() {
      if (!this.form.countryId) {
        this.giftRates = []
        return
      }
      const seq = ++this.ratesRequestSeq
      const params = {
        countryId: Number(this.form.countryId)
      }
      this.rateLoading = true
      this.form.categoryId = undefined
      this.calcResult = null
      giftRates(params).then(res => {
        if (seq !== this.ratesRequestSeq) {
          return null
        }
        const list = this.normalizeGiftRates(res.data)
        this.giftRates = list
        if (list.length === 0) {
          this.$message.warning('当前国家暂无可用卡种，请检查「面额阶梯价」配置')
        }
        return null
      }).catch((err) => {
        if (seq !== this.ratesRequestSeq) {
          return
        }
        this.giftRates = []
        this.$message.error((err && err.message) || '加载卡种失败，请刷新重试')
      }).finally(() => {
        if (seq === this.ratesRequestSeq) {
          this.rateLoading = false
        }
      })
    },
    onCategoryChange() {
      this.calcResult = null
    },
    addCard() {
      this.form.cards.push(emptyCard())
      this.calcResult = null
    },
    removeCard(idx) {
      this.form.cards.splice(idx, 1)
      this.calcResult = null
    },
    onCardImagesChange(idx, val) {
      if (typeof val === 'string') {
        this.$set(this.form.cards[idx], 'imgUrls', val)
      } else if (val && typeof val === 'object') {
        this.$set(this.form.cards[idx], 'imgUrls', val.imgUrls || '')
      }
    },
    doCalculate() {
      if (!this.form.userId) {
        this.$message.warning('请先选择用户')
        return
      }
      if (!this.form.countryId) {
        this.$message.warning('请先选择国家')
        return
      }
      if (!this.form.categoryId) {
        this.$message.warning('请先选择卡种')
        return
      }
      const invalid = this.form.cards.some(c => c.faceValue == null || c.faceValue <= 0 || !c.cardType)
      if (invalid) {
        this.$message.warning('请为每张卡填写卡类型与 Redeem Value')
        return
      }
      const baseParams = {
        categoryId: this.form.categoryId,
        countryId: this.form.countryId
      }
      Promise.all(this.form.cards.map(card => csCalculate(this.form.userId, {
        ...baseParams,
        cardType: card.cardType,
        faceValue: card.faceValue,
        quantity: 1
      }))).then(results => {
        const cardBreakdown = results.map((res, idx) => {
          const amt = Number((res.data && res.data.cashAmount) || 0)
          return `第${idx + 1}张 ${amt}`
        })
        const cashAmount = results.reduce((sum, res) => sum + Number((res.data && res.data.cashAmount) || 0), 0)
        const first = results[0] && results[0].data
        this.calcResult = {
          cashAmount,
          couponBonus: 0,
          currencySymbol: first && first.currencySymbol,
          cardBreakdown
        }
      })
    },
    buildSubmitCards() {
      return this.form.cards.map(c => ({
        faceValue: c.faceValue,
        cardType: c.cardType,
        cardNumber: c.cardNumber,
        cardExpiry: c.cardExpiry,
        cardPin: c.cardPin,
        imgUrls: c.imgUrls
      }))
    },
    submit() {
      if (!this.form.userId) {
        this.$message.warning('请选择用户')
        return
      }
      if (!this.form.countryId) {
        this.$message.warning('请选择国家')
        return
      }
      if (!this.calcResult || !this.calcResult.cashAmount) {
        this.$message.warning('请先计算到账金额')
        return
      }
      const imgUrls = this.form.cards.map(c => c.imgUrls).filter(Boolean).join(',')
      if (!imgUrls) {
        this.$message.warning('请上传至少一张礼品卡图片')
        return
      }
      const cards = this.buildSubmitCards()
      if (cards.some(c => !c.faceValue || c.faceValue <= 0 || !c.cardType)) {
        this.$message.warning('请为每张卡填写卡类型与 Redeem Value')
        return
      }
      this.submitting = true
      csSubmitOrder({
        userId: this.form.userId,
        cashAmount: this.calcResult.cashAmount,
        couponBonus: this.calcResult.couponBonus,
        cards,
        categoryId: this.form.categoryId,
        countryId: this.form.countryId,
        cardType: cards[0].cardType,
        faceValue: cards[0].faceValue,
        quantity: cards.length,
        comment: this.form.comment,
        cardNumber: cards[0] && cards[0].cardNumber,
        cardExpiry: cards[0] && cards[0].cardExpiry,
        cardPin: cards[0] && cards[0].cardPin
      }).then(res => {
        this.$message.success('订单已提交，订单 ID：' + res.data)
        this.calcResult = null
        this.$emit('success', res.data)
      }).finally(() => { this.submitting = false })
    }
  }
}
</script>

<style scoped>
.card-block {
  margin-bottom: 16px;
  padding: 12px 12px 4px;
  background: #fafafa;
  border-radius: 4px;
}
.card-block-title {
  font-weight: 500;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.85);
}
.field-label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
}
.required {
  color: #f5222d;
}
.card-actions {
  text-align: right;
}
.card-images {
  margin-top: 8px;
}
.card-images-label {
  display: block;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
}
.form-tip {
  color: #faad14;
  font-size: 12px;
  margin-top: 4px;
}
.calc-result {
  display: block;
  margin-top: 8px;
  color: #52c41a;
}
.calc-breakdown {
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  margin-left: 4px;
}
</style>
